import { platformApiClient } from '@/services/platform-api-client';

/**
 * PHYSICAL storage usage for a provider.
 *
 * ── WHY THIS IS NOT THE NUMBER THE PROVIDER LIST ALREADY SHOWS ─────────────
 * `ProviderResponseDto.totalStorage` — rendered as "Storage Used" on the
 * providers page — is a sum of `storage_assets.file_size_bytes` for rows whose
 * `provider_id` is that provider. It is a LOGICAL figure that follows OWNERSHIP.
 *
 * After a provider migration flips ownership, the source provider's "Storage
 * Used" collapses towards zero while every one of its physical objects is still
 * there, because the engine retains source objects and deletes nothing. Reading
 * that as physical consumption would claim the migration freed space it did not
 * free.
 *
 * These endpoints ask the PROVIDER instead, and return both figures side by side
 * so the two can never be confused again.
 *
 * `getProviderUsage` is safe to call on render: the server serves a cached
 * reading and only lists the bucket if nothing has been measured yet.
 * `refreshProviderUsage` forces a full listing and is an explicit operator
 * action.
 */

export interface PhysicalUsageDto {
  totalObjects: number;
  /** Decimal string — the value can exceed Number.MAX_SAFE_INTEGER. */
  totalBytes: string;
  measuredAt: string;
  durationMs: number;
}

export interface LogicalUsageDto {
  activeAssets: number;
  activeBytes: string;
  softDeletedAssets: number;
  softDeletedBytes: string;
  totalAssets: number;
  totalBytes: string;
}

export interface UsageSampleDto {
  /** True when the listing hit its page ceiling: the breakdown is partial. */
  truncated: boolean;
  objectsSampled: number;
  largestObjects: Array<{ key: string; bytes: string }>;
  byPrefix: Array<{ prefix: string; objects: number; bytes: string }>;
}

export interface ProviderUsageReportDto {
  providerId: string;
  providerName: string;
  driverId: string;
  /** What the PROVIDER reports. Null when it could not be measured. */
  physical: PhysicalUsageDto | null;
  /** What the CATALOGUE believes it owns. Ownership, not bytes. */
  logical: LogicalUsageDto;
  cached: boolean;
  ageSeconds: number;
  stale: boolean;
  error: string | null;
  sample: UsageSampleDto | null;
  caveats: string[];
}

const BASE = '/platform/storage/providers';

/** Read-only. Serves the cached reading; lists the provider only if none exists. */
export async function getProviderUsage(providerId: string): Promise<ProviderUsageReportDto> {
  const r = await platformApiClient.get(`${BASE}/${providerId}/usage`);
  return r.data as ProviderUsageReportDto;
}

/**
 * Force a fresh measurement. This performs a FULL provider listing, so it is an
 * explicit action, never something a render triggers. It writes nothing to
 * storage and nothing to the database — it replaces an in-memory cache entry.
 *
 * The listing of a very large bucket takes time; the request uses a longer
 * timeout than the client default for that reason.
 */
export async function refreshProviderUsage(providerId: string): Promise<ProviderUsageReportDto> {
  const r = await platformApiClient.post(`${BASE}/${providerId}/usage/refresh`, undefined, {
    timeout: 5 * 60 * 1000,
  });
  return r.data as ProviderUsageReportDto;
}
